<script lang="ts" setup>
import type { Product } from '~/composables/api/payment/useFetchPayment'

import { queryKeys as balanceQueryKeys } from '~/composables/api/balance/useQueryBalance'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<{
  products: Array<Product>
  config?: Array<{ id: string, min?: number, max?: number, description?: string, subtitle?: string }>
  allowConsolidated?: boolean
}>(), {
  allowConsolidated: true,
})

const attrs = useAttrs()
const { t } = useI18n()
const { triggerConfirm } = useConfirm()
const { danger } = useToast()
const { hasValidSubscription } = useSubscription()
const { workspace, activeWorkspaceId } = useAuth()
const { notifyBugsnag } = useReportError()

const {
  listen,
  broadcast,
  cleanup,
  onInit,
  onError,
  open: openSubWindow,
  isOpen: isSubWindowOpen,
  initialized: isSubWindowInitialized,
} = useSubWindow()

onInit(() => {
  broadcast('payment-config', { config: props.config })
})

onError(() => {
  danger(t('payment-button.failure'))
})
const queryClient = useQueryClient()

onMounted(() => {
  listen({
    message: 'payment-purchased',
    handler: () => {
      queryClient.invalidateQueries({ queryKey: balanceQueryKeys._def })
    },
  })
})

const {
  mutateAsync: createPayment,
  isPending: isPaymentPending,
} = useMutationPayment.create()

const isPaymentPendingDelayed = refDelayed(isPaymentPending, false)

const isConsolidated = computed(() => {
  if (!props.allowConsolidated)
    return false

  return workspace.value && hasValidSubscription.value
})

const initPaymentProcess = async () => {
  if (!isConsolidated.value) {
    openSubWindow(
      `/payments?productData=${encodeURIComponent(JSON.stringify(props.products))}`,
      t('payment-button.payment-window-title'),
    )
    return
  }

  if (activeWorkspaceId.value && await triggerConfirm({
    message: t('payment-button.confirm-consolidated'),
  })) {
    try {
      await createPayment({
        workspaceId: activeWorkspaceId.value,
        payment: {
          products: props.products,
          gateway: 'consolidated',
        },
      })
    }
    catch (e) {
      console.error(e)
      notifyBugsnag(e)
      danger(t('unknown-error'))
    }
  }
}
</script>

<template>
  <DTButton
    variant="blue"
    :disabled="isSubWindowOpen || isPaymentPending"
    v-bind="attrs"
    @click="initPaymentProcess"
  >
    <slot />
    <DTSpinner
      v-if="isSubWindowOpen || isPaymentPendingDelayed"
      :size="4"
      :weight="2"
      variant="white"
    />
  </DTButton>

  <Teleport
    v-if="isSubWindowOpen"
    to="body"
  >
    <div
      class="fixed inset-0 z-10 flex items-center justify-center bg-black/25 backdrop-blur-md"
    >
      <div
        class="flex flex-col items-center gap-6 rounded-md bg-white p-4 drop-shadow"
      >
        <DTLoaderDots class="mt-4" />
        <div class="flex max-w-sm flex-col text-center">
          <span>{{ t('payment-button.popup.title') }}</span>
          <span class="text-sm text-gray-500">{{ t('payment-button.popup.text') }}</span>
          <span
            v-if="!isSubWindowInitialized"
            class="mt-2 text-xs text-gray-500"
          >
            <i>{{ t('payment-button.popup.guide') }}</i>
          </span>
        </div>
        <DTButton
          class="w-max"
          @click="cleanup"
        >
          {{ t('cancel') }}
        </DTButton>
      </div>
    </div>
  </Teleport>
</template>
